// edgeHierarchy.js
//
// Hierarchical edge overlay (parentKey) for:
// - Built-in pairs: Project→Task, Task→Task
// - Exclusive containment: child type.parentTypeKey === parent.type
// Ordinary related edges omit parentKey.

const HIERARCHY_PAIRS = Object.freeze([
  Object.freeze({ parentType: 'PROJECT', childType: 'TASK' }),
  Object.freeze({ parentType: 'TASK', childType: 'TASK' }),
]);

/** @type {(typeKey: unknown) => object|null|undefined} */
let typeLookup = () => null;

/**
 * Register a type-def lookup used when resolving exclusive parentTypeKey.
 * Wired from NodeTypesCacheService so this module stays free of Firebase imports.
 *
 * @param {(typeKey: unknown) => object|null|undefined} fn
 * @returns {void}
 */
export function configureEdgeHierarchyTypeResolver(fn) {
  typeLookup = typeof fn === 'function' ? fn : () => null;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeEdgeTypeKey(value) {
  return String(value || '').trim().toUpperCase();
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function normalizeParentTypeKey(value) {
  const key = normalizeEdgeTypeKey(value);
  return key || null;
}

/**
 * Whether a parent/child type pair may use hierarchical edges.
 *
 * @param {unknown} parentType
 * @param {unknown} childType
 * @param {object} [options]
 * @param {string|null|undefined} [options.childParentTypeKey] - Child type's exclusive parentTypeKey
 * @returns {boolean}
 */
export function isAllowedHierarchyTypePair(parentType, childType, options = {}) {
  const parent = normalizeEdgeTypeKey(parentType);
  const child = normalizeEdgeTypeKey(childType);
  if (!parent || !child) return false;

  if (HIERARCHY_PAIRS.some((pair) => (
    pair.parentType === parent && pair.childType === child
  ))) {
    return true;
  }

  const exclusiveParent = normalizeParentTypeKey(options.childParentTypeKey);
  return Boolean(exclusiveParent && exclusiveParent === parent);
}

/**
 * Resolves parentKey when linking two nodes with known types and roles.
 * Returns null when the link should stay a flat related edge.
 *
 * @param {object} params
 * @param {string} params.parentNodeKey - Intended parent endpoint (anchor).
 * @param {unknown} params.parentType
 * @param {string} params.childNodeKey - Intended child endpoint.
 * @param {unknown} params.childType
 * @param {string|null|undefined} [params.childParentTypeKey]
 * @returns {string|null}
 */
export function resolveHierarchyParentKey({
  parentNodeKey,
  parentType,
  childNodeKey,
  childType,
  childParentTypeKey,
}) {
  const parentKey = String(parentNodeKey || '').trim();
  const childKey = String(childNodeKey || '').trim();
  if (!parentKey || !childKey || parentKey === childKey) return null;
  if (!isAllowedHierarchyTypePair(parentType, childType, { childParentTypeKey })) {
    return null;
  }
  return parentKey;
}

/**
 * @param {unknown} childType
 * @param {string|null|undefined} childParentTypeKey
 * @returns {string|null}
 */
function resolveChildParentTypeKey(childType, childParentTypeKey) {
  const explicit = normalizeParentTypeKey(childParentTypeKey);
  if (explicit) return explicit;
  try {
    return normalizeParentTypeKey(typeLookup(childType)?.parentTypeKey);
  } catch {
    return null;
  }
}

/**
 * Builds upsertEdge options including parentKey when the type pair allows hierarchy.
 *
 * @param {object} params
 * @param {object|null|undefined} params.parentNode - Anchor / intended parent.
 * @param {object|null|undefined} params.childNode - Intended child.
 * @param {string|null|undefined} params.baseKey
 * @param {string|null|undefined} [params.childParentTypeKey]
 * @returns {{ baseKey: string|null, parentKey?: string }}
 */
export function buildHierarchyEdgeOptions({
  parentNode,
  childNode,
  baseKey = null,
  childParentTypeKey,
} = {}) {
  const options = {
    baseKey: baseKey
      || parentNode?.baseKey
      || childNode?.baseKey
      || null,
  };
  const resolvedChildParentTypeKey = resolveChildParentTypeKey(
    childNode?.type,
    childParentTypeKey,
  );
  const parentKey = resolveHierarchyParentKey({
    parentNodeKey: parentNode?.key,
    parentType: parentNode?.type,
    childNodeKey: childNode?.key,
    childType: childNode?.type,
    childParentTypeKey: resolvedChildParentTypeKey,
  });
  if (parentKey) options.parentKey = parentKey;
  return options;
}

/**
 * Given an edge doc and a child node key, return parentKey when this edge
 * encodes hierarchy for that child.
 *
 * @param {object|null|undefined} edge
 * @param {string} childNodeKey
 * @returns {string|null}
 */
export function readHierarchyParentKey(edge, childNodeKey) {
  const childKey = String(childNodeKey || '').trim();
  const parentKey = String(edge?.parentKey || '').trim();
  if (!childKey || !parentKey) return null;

  const source = String(edge?.sourceNodeKey || '').trim();
  const target = String(edge?.targetNodeKey || '').trim();
  if (!source || !target) return null;
  if (parentKey !== source && parentKey !== target) return null;
  if (childKey !== source && childKey !== target) return null;
  if (parentKey === childKey) return null;

  return parentKey;
}

/**
 * Child endpoint for a hierarchical edge (endpoint that is not parentKey).
 *
 * @param {object|null|undefined} edge
 * @returns {string|null}
 */
export function readHierarchyChildKey(edge) {
  const parentKey = String(edge?.parentKey || '').trim();
  if (!parentKey) return null;
  const source = String(edge?.sourceNodeKey || '').trim();
  const target = String(edge?.targetNodeKey || '').trim();
  if (parentKey === source && target) return target;
  if (parentKey === target && source) return source;
  return null;
}

export const EDGE_HIERARCHY_PAIRS = HIERARCHY_PAIRS;
